import { useState, useCallback, useRef } from 'react';
import {
  Requirement,
  RequirementAnalysis,
  AnalysisSession,
  AnalysisJob,
} from '../types';
import * as api from '../api/api';
import { normalizeSeverity } from '../utils/severity';
import {
  deleteThread,
  forceApproveThread,
  listAllThreads,
} from '../../requirement-lifecycle/api';
import type { ApprovalWarning } from '../../requirement-lifecycle/api';
import {
  isMergeLockedSource,
  type RequirementThread,
} from '../../requirement-lifecycle/types';

/**
 * What one force-approve attempt did.
 *
 * `approved` and `warnings` alone are not enough to decide what to show. The
 * ASPICE fast-check gate answers HTTP 200 without approving when it finds
 * issues — a normal outcome the user resolves by reading the warnings. A
 * missing thread id or a failed request is not: there is nothing to read, and
 * re-sending with confirm would just fail again. `reason` separates the two so
 * the caller does not put an empty warnings dialog in front of an error.
 */
export type ForceApproveOutcome =
  | { reqId: string; approved: true }
  | {
      reqId: string;
      approved: false;
      reason: 'gate';
      warnings: ApprovalWarning[];
    }
  | { reqId: string; approved: false; reason: 'error' };

/**
 * Map a lifecycle thread onto the Doctor's working model.
 *
 * The id is derived from the thread rather than its position in the list, so a
 * reload does not silently re-label requirements — a positional `RQ-003` would
 * point at a different requirement after anything is added or deleted.
 */
function toRequirement(thread: RequirementThread): Requirement {
  return {
    id: thread.externalRef || `RQ-${thread.threadId}`,
    // `name` is truncated at 80 characters; `text` is the real requirement.
    text: thread.text || thread.name,
    // The backend status is carried through as-is. Collapsing it to
    // APPROVED-or-WIP used to hide DRAFT and both PENDING_* states behind
    // "WIP", which is what made the Doctor offer actions the API then refused.
    status: thread.status,
    aspiceScore: thread.aspiceScore,
    incoseScore: thread.incoseScore,
    category: thread.category,
    createdAt: thread.createdAt,
    updatedAt: thread.updatedAt,
    threadId: thread.threadId,
    // The CURRENT version, not acceptedVersionId: the latter stays "0" until a
    // thread is approved, and analysis jobs are correlated back to a
    // requirement by version id. Using the accepted id left every freshly
    // loaded requirement without one, so analysis results matched nothing.
    versionId: thread.currentVersionId,
    mergeLocked: isMergeLockedSource(thread),
  };
}

export const useRequirementStore = () => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentReqId, setCurrentReqId] = useState<string | null>(null);
  const [currentReqThreadId, setCurrentReqThreadId] = useState<string | null>(
    null,
  );
  const [lastFixedText, setLastFixedText] = useState<string | null>(null);
  // Why the backend returned the requirement unchanged, in the request locale.
  // Empty when it actually rewrote the text.
  const [lastFixNotes, setLastFixNotes] = useState<string | null>(null);
  // Suggestions the last fix could not apply, with their localised reasons.
  const [lastFixSkipped, setLastFixSkipped] = useState<
    api.FixSkippedSuggestion[]
  >([]);
  const [analysisSession, setAnalysisSession] =
    useState<AnalysisSession | null>(null);

  const [loading, setLoading] = useState(false);
  // Every load takes a ticket; only the newest one may write to the store.
  // Without this, the load started when the screen mounts can resolve after the
  // reload that follows an import and overwrite it — the rows the user has just
  // imported would vanish on a slow connection. Importing also takes a ticket,
  // so an in-flight load cannot overwrite its result either.
  const genRef = useRef(0);

  // Load the project's existing requirements.
  //
  // Until this existed the Doctor only ever showed what the current session had
  // just imported, so leaving the screen lost the list and there was no way
  // back to requirements imported earlier.
  //
  // Returns the number of requirements loaded, or null when the load failed, so
  // the caller can decide which screen to open without reading stale state.
  const loadProjectRequirements = useCallback(
    async (projectId: number): Promise<number | null> => {
      const gen = ++genRef.current;
      setApiError(null);
      setLoading(true);
      try {
        // Follow pagination to the end. Stopping at one page would silently
        // hide everything past it, and the Doctor acts on this list: redundancy
        // detection over "everything not selected" would quietly compare a
        // subset, and the "not compared" warning would be computed against a
        // list that is missing rows.
        const { threads: collected } = await listAllThreads({ projectId });
        if (gen !== genRef.current) return null;
        const loaded = collected.map(toRequirement);
        setRequirements(loaded);
        // Ids are rebuilt from the server response, so anything selected under
        // the previous list would now point at nothing.
        setSelectedIds(new Set());
        return loaded.length;
      } catch (error) {
        if (gen !== genRef.current) return null;
        setApiError(
          error instanceof Error
            ? error.message
            : 'Failed to load requirements',
        );
        return null;
      } finally {
        if (gen === genRef.current) setLoading(false);
      }
    },
    [],
  );

  // Populate the store from a successful import, preserving the backend
  // thread/version ids so analyze/fix can target the real records.
  const setRequirementsFromImport = useCallback(
    (results: import('../api/api').ImportRequirementResult[]) => {
      // Invalidate any load still in flight: its result predates this import.
      genRef.current += 1;
      const now = new Date().toISOString();
      setRequirements(prev => {
        const nextIndex = prev.length + 1;
        const imported: Requirement[] = results.map((r, idx) => ({
          id: `RQ-${String(nextIndex + idx).padStart(3, '0')}`,
          text: r.content?.text?.text ?? '',
          // Import creates the thread in DRAFT. The backend moves it to WIP
          // once an analysis completes, which is picked up on the next load.
          status: 'DRAFT' as const,
          createdAt: now,
          updatedAt: now,
          threadId: r.requirementThreadId,
          versionId: r.requirementVersionId,
        }));
        return [...prev, ...imported];
      });
    },
    [],
  );

  const updateAnalysis = useCallback(
    (reqId: string, analysis: RequirementAnalysis) => {
      setRequirements(prev =>
        prev.map(req =>
          req.id === reqId
            ? {
                ...req,
                analysis,
                updatedAt: new Date().toISOString(),
              }
            : req,
        ),
      );
    },
    [],
  );

  // Map finished analysis jobs (keyed by backend versionId) onto the matching
  // requirements so the S4 review screen shows real scores + broken rules: a
  // completed job sets `analysis`, a failed one sets `analysisFailure` and
  // drops any earlier `analysis`. Jobs still pending/running, or cancelled,
  // are ignored.
  const applyAnalysisResults = useCallback(
    (jobs: import('../api/api').AnalysisJobDto[]) => {
      setRequirements(prev =>
        prev.map(req => {
          const job = jobs.find(
            j =>
              req.versionId !== undefined &&
              j.requirementVersionId === req.versionId,
          );
          if (!job) {
            return req;
          }
          if (job.status === 'ANALYSIS_JOB_STATUS_FAILED') {
            // The latest attempt failed, so an analysis from an earlier
            // attempt no longer describes what the user just asked for.
            return {
              ...req,
              analysis: undefined,
              analysisFailure: { message: job.errorMessage || undefined },
            };
          }
          if (job.status !== 'ANALYSIS_JOB_STATUS_COMPLETED') {
            return req;
          }
          const aspiceScore = job.aspiceScore ?? 0;
          const analysis: RequirementAnalysis = {
            aspiceScore,
            incoseScore: job.incoseScore ?? 0,
            violations: job.violations ?? 0,
            // Same as the list API: a completed analysis without a category
            // is "Unknown", never "not analysed".
            category: job.category || 'Unknown',
            categoryScore: aspiceScore,
            brokenRules: (job.brokenRules ?? []).map(
              (r: api.BrokenRuleDto) => ({
                rule: r.ruleId,
                violation: r.violation,
                fix: r.fix,
                severity: normalizeSeverity(r.severity),
              }),
            ),
          };
          return {
            // Only the analysis payload is written here. Writing a status too
            // used to turn an APPROVED requirement back into "Analyzed", which
            // read as "作業中" on screen while the backend still had the thread
            // APPROVED. The backend only ever moves a thread on analysis when
            // it is DRAFT, and that is picked up on the next load.
            ...req,
            analysis,
            analysisFailure: undefined,
            updatedAt: new Date().toISOString(),
          };
        }),
      );
    },
    [],
  );

  // newVersionId is the version the edit created on the backend. It must be carried
  // over: applyAnalysisResults matches jobs to requirements by versionId, and a
  // re-analysis runs against the new version. Leaving the old id here means the next
  // analysis produces a job that matches nothing, and the screen keeps showing the
  // scores from before the fix while the API already reports the new ones.
  const applyFix = useCallback(
    (reqId: string, _before: string, after: string, newVersionId?: string) => {
      setRequirements(prev =>
        prev.map(req =>
          req.id === reqId
            ? {
                ...req,
                text: after,
                versionId: newVersionId ?? req.versionId,
                // The fix produced a new version, so the analysis on screen
                // describes text that no longer exists. Keeping it made the
                // "Fix" guard useless after the first fix — the button stayed
                // enabled on a version the backend has no analysis for, and
                // pressing it answered 400 analysis-not-run. The carried-over
                // scores and category go for the same reason.
                analysis: undefined,
                analysisFailure: undefined,
                aspiceScore: undefined,
                incoseScore: undefined,
                category: undefined,
                updatedAt: new Date().toISOString(),
              }
            : req,
        ),
      );
    },
    [],
  );

  const markApproved = useCallback((reqId: string) => {
    setRequirements(prev =>
      prev.map(r =>
        r.id === reqId
          ? {
              ...r,
              status: 'APPROVED' as const,
              updatedAt: new Date().toISOString(),
            }
          : r,
      ),
    );
  }, []);

  /** Approve a WIP requirement. DRAFT is refused by the backend (409). */
  const approveRequirement = useCallback(
    async (reqId: string): Promise<boolean> => {
      const req = requirements.find(r => r.id === reqId);
      if (!req?.threadId) {
        setApiError('Cannot approve: missing backend thread id');
        return false;
      }
      try {
        await api.approveThread(req.threadId);
        markApproved(reqId);
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to approve requirement';
        setApiError(message);
        return false;
      }
    },
    [requirements, markApproved],
  );

  /**
   * Approve a requirement whatever its state, including DRAFT.
   *
   * This is what the "force approve" action must call — approveRequirement
   * refuses DRAFT, and import creates threads in DRAFT, so pointing the action
   * at it made force-approve fail on exactly the requirements it exists for.
   *
   * The ASPICE fast-check gate answers HTTP 200 with `confirmed: false` and
   * leaves the thread alone, so the caller has to branch on the result rather
   * than treat a resolved promise as success. Pass `confirm` once the user has
   * seen the warnings and chosen to approve regardless.
   */
  const forceApproveRequirement = useCallback(
    async (reqId: string, confirm = false): Promise<ForceApproveOutcome> => {
      const req = requirements.find(r => r.id === reqId);
      if (!req?.threadId) {
        setApiError('Cannot approve: missing backend thread id');
        return { reqId, approved: false, reason: 'error' };
      }
      try {
        const result = await forceApproveThread(req.threadId, confirm);
        if (result.confirmed) {
          markApproved(reqId);
          return { reqId, approved: true };
        }
        return {
          reqId,
          approved: false,
          reason: 'gate',
          warnings: result.warnings,
        };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to approve requirement';
        setApiError(message);
        return { reqId, approved: false, reason: 'error' };
      }
    },
    [requirements, markApproved],
  );

  const unapproveRequirement = useCallback(
    async (reqId: string): Promise<boolean> => {
      const req = requirements.find(r => r.id === reqId);
      if (!req?.threadId) {
        setApiError('Cannot unapprove: missing backend thread id');
        return false;
      }
      try {
        await api.unapproveThread(req.threadId);
        setRequirements(prev =>
          prev.map(r =>
            r.id === reqId
              ? {
                  ...r,
                  status: 'WIP' as const,
                  updatedAt: new Date().toISOString(),
                }
              : r,
          ),
        );
        return true;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to unapprove requirement';
        setApiError(message);
        return false;
      }
    },
    [requirements],
  );

  // Deleting only from local state used to be invisible, because the list was
  // never re-read from the server. Now that it is, a "deleted" requirement came
  // straight back on the next load — so the soft delete has to be persisted.
  const deleteRequirement = useCallback(
    async (reqId: string): Promise<boolean> => {
      const req = requirements.find(r => r.id === reqId);
      if (req?.threadId) {
        try {
          await deleteThread(req.threadId);
        } catch (error) {
          setApiError(
            error instanceof Error ? error.message : 'Failed to delete',
          );
          return false;
        }
      }
      setRequirements(prev => prev.filter(r => r.id !== reqId));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(reqId);
        return next;
      });
      return true;
    },
    [requirements],
  );

  const toggleSelect = useCallback((reqId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(reqId)) {
        next.delete(reqId);
      } else {
        next.add(reqId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(
    (checked: boolean, displayedIds?: string[]) => {
      if (checked) {
        setSelectedIds(new Set(displayedIds ?? requirements.map(r => r.id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [requirements],
  );

  // Returns true on success, false on error (so callers can check without
  // reading stale React state — setApiError is async and the updated value
  // is not visible in the same render cycle as the await).
  const startAnalysisSession = useCallback(
    // `projectId` is required on purpose: a default here is what let the whole
    // screen quietly operate on project 1 regardless of which project the user
    // was in. Callers must pass the active project explicitly.
    async (selectedReqIds: string[], projectId: number): Promise<boolean> => {
      setApiError(null);
      try {
        // Resolve the selected UI ids to the real backend thread ids captured at import.
        const threadIds = selectedReqIds
          .map(id => requirements.find(r => r.id === id)?.threadId)
          .filter((tid): tid is string => typeof tid === 'string');

        if (threadIds.length === 0) {
          setApiError(
            'No backend thread ids found for the selected requirements',
          );
          return false;
        }

        const response = await api.analyzeRequirements(projectId, threadIds);

        // Convert API response (camelCase, enum-name statuses) to AnalysisJob format.
        // Match each job back to its UI requirement id via versionId (not positional
        // index) — the backend may reorder jobs relative to the request order.
        const jobs: AnalysisJob[] = response.jobs.map(job => ({
          id: job.id,
          requirementId:
            requirements.find(r => r.versionId === job.requirementVersionId)
              ?.id ?? `RQ-${job.requirementVersionId}`,
          step: job.step,
          status: api.normalizeJobStatus(job.status),
          progress: job.progress,
          startedAt: job.startedAt ?? new Date().toISOString(),
        }));

        setAnalysisSession({
          id: response.sessionId,
          totalRequirements: selectedReqIds.length,
          completedRequirements: 0,
          jobs,
          startedAt: new Date().toISOString(),
        });
        return true;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to start analysis';
        setApiError(message);
        console.error('Analysis start error:', error);
        return false;
      }
    },
    [requirements],
  );

  // Replace the whole analysis session (used by the S2 polling loop to push the
  // latest job statuses/progress from the backend into state).
  const replaceAnalysisSession = useCallback((session: AnalysisSession) => {
    setAnalysisSession(session);
  }, []);

  const updateJobStatus = useCallback(
    (jobId: string, update: Partial<AnalysisJob>) => {
      setAnalysisSession(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          jobs: prev.jobs.map(job =>
            job.id === jobId ? { ...job, ...update } : job,
          ),
        };
      });
    },
    [],
  );

  const completeAnalysisSession = useCallback(() => {
    setAnalysisSession(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        completedRequirements: prev.jobs.filter(j => j.status === 'completed')
          .length,
      };
    });
  }, []);

  const cancelAnalysisSession = useCallback(() => {
    setAnalysisSession(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        cancelledAt: new Date().toISOString(),
      };
    });
  }, []);

  return {
    requirements,
    selectedIds,
    currentReqId,
    setCurrentReqId,
    currentReqThreadId,
    setCurrentReqThreadId,
    lastFixedText,
    setLastFixedText,
    lastFixNotes,
    setLastFixNotes,
    lastFixSkipped,
    setLastFixSkipped,
    analysisSession,
    replaceAnalysisSession,
    apiError,
    clearApiError: () => setApiError(null),
    loading,
    loadProjectRequirements,
    setRequirementsFromImport,
    updateAnalysis,
    applyAnalysisResults,
    applyFix,
    approveRequirement,
    forceApproveRequirement,
    unapproveRequirement,
    deleteRequirement,
    toggleSelect,
    selectAll,
    startAnalysisSession,
    updateJobStatus,
    completeAnalysisSession,
    cancelAnalysisSession,
  };
};
